import { currentUrl, isPage, type PageOrError, replaceState, translate, URLX } from '@avensia/excite';
import * as React from 'react';
import { PRODUCT_CONFIG_URL_PARAMS } from './product-configuration-selectors';
import {
  type PropType,
  type Selector,
  type OptionType,
  type ProductPage,
  DefaultSelectorId,
  SelectType,
} from './ProductVariantsSelectorProvider';

import type { JSX } from "react";

export function useDefaultVariantSelector(
  props: React.PropsWithChildren<PropType>,
  isSmallViewport: boolean,
  addOrUpdate: (selector: Selector) => void,
  variantTransform: (option: OptionType, isSelectedValue: boolean, placeholder?: string) => JSX.Element,
) {
  const { variants } = props;
  const options = variants?.map(v => ({ value: v.articleNumber, text: v.name, variant: v }));
  const value = props.currentVariant?.articleNumber ?? props.currentVariant?.articleNumber[0];
  const selectedOption = options?.find(o => o.value === value);
  const extendPartialCache = React.useCallback(
    (page: PageOrError) => {
      if (isPage(page)) {
        (page as ProductPage).breadcrumbs = props.breadcrumbs;
        (page as ProductPage).variants = props.variants;
        (page as ProductPage).description = props.description;
        return page;
      }
    },
    [props.breadcrumbs, props.description, props.variants],
  );

  const onChangeOption = React.useCallback(
    (selectedArticleNumber: string) => {
      const selectedVariant = variants.find(({ articleNumber }) => articleNumber === selectedArticleNumber);

      if (selectedVariant) {
        isSmallViewport && scrollTo(0, 0);
        const params = currentUrl().searchParams.getAll(PRODUCT_CONFIG_URL_PARAMS);
        const url = new URLX(selectedVariant.url);
        params.forEach(p => url.searchParams.append(PRODUCT_CONFIG_URL_PARAMS, p));
        replaceState(url, { extendPartialCache });
      }
    },
    [extendPartialCache, isSmallViewport, variants],
  );

  const selectedTransform = React.useCallback(
    (option: NordicNest.Api.SelectOptionApiModel) => variantTransform(option, true),
    [variantTransform, variants],
  );
  const optionTransform = React.useCallback(
    (option: NordicNest.Api.SelectOptionApiModel) => variantTransform(option, false),
    [variantTransform, variants],
  );

  const placeholder = translate(t => t.productPage.selectVariant);
  if (
    !(props.variesOn && Object.keys(props.variesOn).length > 0) &&
    !(props.packageParts && props.packageParts.length > 0)
  ) {
    addOrUpdate({
      id: DefaultSelectorId,
      value,
      selectedOption,
      options,
      onChangeOption,
      selectedTransform,
      optionTransform,
      showVariantSelect: props.showVariantSelect || options?.length > 1,
      type: SelectType.Variant,
      additionalPrice: { inclVat: 0, exclVat: 0, vatPercent: 0 },
      placeholder,
      selectedVariant: variants?.find(v => v.articleNumber == value),
    });
  }
}
